import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  AccessibilityInfo,
  findNodeHandle,
  Image,
  ScrollView,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import {noop} from 'lodash';

import OptaviaConnectLogo from '/asset/image/connect_logo.png';
import Icon from '/component/base/Icon';
import {CreatePinInput} from '/component/base/PinInput/inputs/CreatePinInput/CreatePinInput';
import {EnterPinInput} from '/component/base/PinInput/inputs/EnterPinInput/EnterPinInput';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';
import ModalHeader from '/component/layout/ModalHeader';
import {handlePinModalScreen} from '/component/partial/PinModal/PinModal.util';
import {useHeader} from '/component/screen/Login/LoginForm/Header/Header.hook';
import {DashboardID, Modal} from '/constant/TestIDs';
import {HAS_SETUP_PIN, SHOW_SUCCESS_MODAL, USER_PIN} from '/enum/storage.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {useStorage} from '/util/async-storage/useStorage';
import {checkPin} from '/util/bcrypt.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './PinModal.style';

interface PinModalProps {
  handlePinLogin: (pin: string) => void;
  showCreatePinModal: boolean;
  skipButton: () => void;
  onClose: () => void;
  onSubmit: (pin: string) => void;
  onFirstLogin: boolean;
  showSuccess: boolean;
  lastPaidRank: string;
}

export const PinModal: React.FC<PinModalProps> = ({
  handlePinLogin = noop,
  showCreatePinModal = false,
  skipButton = noop,
  onClose = noop,
  onSubmit = noop,
  onFirstLogin = false,
  lastPaidRank = '',
}) => {
  const [pinSetup, setPinSetup] = useState<boolean | undefined>();
  const [pinCodeHash, setPinCodeHash] = useState<string | undefined>();
  const [localShowSuccess, setLocalShowSuccess] = useState(false);
  const logoRef = useRef<View>(null);
  const logoFocusTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {ts} = useTranslation('screen.pinModal');
  const {gutterStyle, onLayout} = useHeader();

  const {getValue: getPinOptInValue} = useStorage({
    key: HAS_SETUP_PIN,
    defaultValue: false,
  });

  const {getValue: getUserPinValue} = useStorage({
    key: USER_PIN,
    defaultValue: false,
  });

  const {getValue: getShowSuccess} = useStorage({
    key: SHOW_SUCCESS_MODAL,
    defaultValue: false,
  });

  useEffect(() => {
    getShowSuccess().then(val => setLocalShowSuccess(val));
  }, [getShowSuccess]);

  useEffect(() => {
    handlePinModalScreen({
      getUserPinValue,
      getPinOptInValue,
      setPinCodeHash,
      setPinSetup,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinSetup]);

  const [contentReady, setContentReady] = useState(false);
  const successRef = React.useRef<View>(null);
  const [hasAnnounced, setHasAnnounced] = useState(false);

  useEffect(() => {
    if (!localShowSuccess || hasAnnounced) return;

    setHasAnnounced(true);
    setContentReady(false);

    setTimeout(() => {
      AccessibilityInfo.announceForAccessibility(
        ts('pinChangedSuccess', 'PIN has been changed successfully'),
      );

      setTimeout(() => {
        setContentReady(true);

        setTimeout(() => {
          const handle = findNodeHandle(successRef.current);
          if (handle != null) {
            AccessibilityInfo.setAccessibilityFocus(handle);
          }
        }, 1800);
      }, 1300);
    }, 300);
  }, [localShowSuccess, hasAnnounced, ts]);

  const handleEnterPinSuccess = useCallback(() => {
    if (showCreatePinModal) {
      setLocalShowSuccess(true);
    } else {
      skipButton();
    }
  }, [showCreatePinModal, skipButton]);

  const focusLogoForScreenReader = useCallback(() => {
    // move focus to the logo so VoiceOver announces it before other content
    if (logoFocusTimeout.current) {
      clearTimeout(logoFocusTimeout.current);
    }

    AccessibilityInfo.isScreenReaderEnabled().then(isEnabled => {
      if (
        !isEnabled ||
        !logoRef.current ||
        typeof AccessibilityInfo.setAccessibilityFocus !== 'function'
      ) {
        return;
      }

      const handle = findNodeHandle(logoRef.current);
      if (!handle) {
        return;
      }

      logoFocusTimeout.current = setTimeout(() => {
        AccessibilityInfo.setAccessibilityFocus(handle);
      }, 150);
    });
  }, []);

  useEffect(() => {
    if (!localShowSuccess) {
      focusLogoForScreenReader();
    }

    return () => {
      if (logoFocusTimeout.current) {
        clearTimeout(logoFocusTimeout.current);
        logoFocusTimeout.current = null;
      }
    };
  }, [focusLogoForScreenReader, localShowSuccess, showCreatePinModal]);

  return (
    <>
      {!localShowSuccess ? (
        <ModalHeader
          accessibilityLabel={
            showCreatePinModal
              ? ts('screen.pinModal', 'Please create a new 4-digit PIN')
              : ts('screen.pinModal', 'Login with your 4 digit PIN')
          }
          accessibilityViewIsModal
          testID={Modal.pinModal}
          onClose={skipButton}
          style={{base: styles.base}}
          actionLabel={ts('skip', 'Skip')}
          closeIconName={'close'}
          onActionPress={skipButton}>
          <KeyboardAvoidingView>
            <ScrollView
              style={styles.content}
              keyboardShouldPersistTaps="always"
              accessible={false}>
              <View
                ref={logoRef}
                accessible={true}
                accessibilityLabel="Optavia Coach"
                importantForAccessibility="yes"
                style={styles.logoContainer}>
                <Image
                  style={styles.logoIcon}
                  source={OptaviaConnectLogo}
                  resizeMode="contain"
                  onLayout={onLayout}
                  accessibilityElementsHidden={true}
                  importantForAccessibility="no"
                />
                <Text
                  style={styles.optaviaText}
                  accessibilityElementsHidden={true}
                  importantForAccessibility="no">
                  <Text style={styles.optaviaBold} isBold>
                    OPTA
                  </Text>
                  <Text style={styles.optaviaRegular}>VIA</Text>
                  <Text style={styles.registered}>®</Text>
                </Text>

                <Text
                  style={styles.coachText}
                  accessibilityElementsHidden={true}
                  importantForAccessibility="no">
                  COACH
                </Text>
              </View>
              <Gutter style={gutterStyle as StyleProp<ViewStyle>} />
              <Gutter style={styles.spacer} />
              {!showCreatePinModal ? (
                <EnterPinInput
                  skipButton={skipButton}
                  handlePinLogin={handlePinLogin}
                  theme="dark"
                  textVariant="heading"
                  enterTitle={ts(
                    'loginWithYourFourDigitPin',
                    'Login with your 4 digit PIN',
                  )}
                  errorTitle={ts('tryAgain', 'Try Again')}
                  onSubmit={onSubmit}
                  checkPinCode={async pinCode =>
                    await checkPin(pinCode, pinCodeHash)
                  }
                  lastPaidRank={lastPaidRank}
                  onSuccess={handleEnterPinSuccess}
                />
              ) : (
                <CreatePinInput
                  onClose={onClose}
                  onFirstLogin={onFirstLogin}
                  theme="dark"
                  textVariant="heading"
                  createTitle={ts(
                    'pleaseCreateANew4DigitPin',
                    'Please create a new 4-digit PIN',
                  )}
                  confirmTitle={ts(
                    'pleaseReEnterANew4DigitPin',
                    'Please re-enter a new 4-digit PIN',
                  )}
                  onSuccess={() => {
                    setHasAnnounced(false);
                    setContentReady(false);
                    setLocalShowSuccess(true);
                  }}
                />
              )}
            </ScrollView>
          </KeyboardAvoidingView>
        </ModalHeader>
      ) : (
        contentReady && (
          <View
            accessible={false}
            accessibilityViewIsModal
            style={styles.successModalContainer}>
            <View
              style={styles.successContainer}
              ref={successRef}
              accessible={true}
              importantForAccessibility="auto">
              <Icon
                name="circle-checkmark-filled"
                style={{...styles.itemIcon, ...styles.systemItemIcon}}
              />
              <Text
                style={styles.successText}
                {...testProperties(DashboardID.dashboardPinAddedTitle)}
                accessibilityLabel="Success">
                {ts('success', 'Success!')}
              </Text>
            </View>
            <View style={styles.horizontalRule} />
            <View accessible={true}>
              <Text
                style={styles.thankYouText}
                {...testProperties(DashboardID.dashboardPinAddedMessage)}
                accessibilityLabel="Thanks! Your PIN has been added. ">
                {ts(
                  'thanksYourPinHasBeenAdded',
                  'Thanks! Your PIN has been added.',
                )}
              </Text>
            </View>
            <View accessible={true}>
              <Text
                style={styles.thankYouText2}
                {...testProperties(DashboardID.dashboardPinAddedSecondMessage)}
                accessibilityLabel="You can turn this feature off in your app setting at any time.">
                {ts(
                  'successDescription',
                  'You can turn this feature off in your app setting at any time.',
                )}
              </Text>
            </View>
            <View accessible={false}>
              <TouchableOpacity
                accessible={true}
                accessibilityLabel="Done"
                accessibilityRole="button"
                onPress={onClose}
                {...testProperties(null, false)}>
                <Text
                  style={styles.doneButton}
                  {...testProperties(DashboardID.dashboardPinAddedDoneButton)}>
                  DONE
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )
      )}
    </>
  );
};
